import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  Activity,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Loader,
  RefreshCw,
  WifiOff,
  Server,
} from 'lucide-react';
import {   AreaChart, Area,   XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell } from 'recharts';
import { adminApiClient } from '../services/adminApiClient';
import { activityService } from '../services/activityService';
import adminAnalyticsService from '../services/adminAnalyticsService';

const PLAN_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

/** Returns the last 6 calendar months as { label, year, month } */
const getLast6Months = () => {
  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      label: d.toLocaleString('default', { month: 'short' }),
      year: d.getFullYear(),
      month: d.getMonth() + 1, // 1-indexed
    });
  }
  return months;
};

/** Format a date as a relative "X ago" string. */
const timeAgo = (dateStr) => {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

/** Map eventType to a display type for icon/color. */
const mapActivityType = (eventType) => {
  if (!eventType) return 'other';
  const et = eventType.toUpperCase();
  if (et.includes('LOGIN') || et.includes('REGISTER') || et.includes('SIGNUP')) return 'signup';
  if (et.includes('UPGRADE') || et.includes('ACTIVATE') || et.includes('SUBSCRI')) return 'upgrade';
  if (et.includes('TICKET') || et.includes('SUPPORT')) return 'support';
  if (et.includes('CANCEL') || et.includes('DELETE') || et.includes('DEACTIVAT')) return 'cancellation';
  return 'other';
};

/** Format uptime seconds into "Xd Xh Xm" */
const formatUptime = (seconds) => {
  if (seconds == null) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const AdminDashboard = () => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [growthData, setGrowthData] = useState([]);
  const [billingStats, setBillingStats] = useState(null);
  const [subStats, setSubStats] = useState(null);

  // System health
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthCheckedAt, setHealthCheckedAt] = useState(null);
  const healthIntervalRef = useRef(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await adminApiClient.get('/api/admin/stats');
        setStatsData(data);
      } catch (err) {
        console.error('Error fetching admin stats:', err);
        setError(err.message);
        setStatsData({
          totalCompanies: null,
          activeCompanies: null,
          totalPlatformUsers: null,
          openSupportTickets: null
        });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    Promise.allSettled([
      adminAnalyticsService.getGrowth(),
      adminAnalyticsService.getBillingAdminStats(),
      adminAnalyticsService.getSubscriptionStats(),
    ]).then(([growthRes, billingRes, subRes]) => {
      if (growthRes.status === 'fulfilled') setGrowthData(growthRes.value || []);
      else console.error('Failed to load growth data:', growthRes.reason);
      if (billingRes.status === 'fulfilled') setBillingStats(billingRes.value);
      else console.error('Failed to load billing stats:', billingRes.reason);
      if (subRes.status === 'fulfilled') setSubStats(subRes.value);
      else console.error('Failed to load subscription stats:', subRes.reason);
    });
  }, []);

  useEffect(() => {
    activityService.getActivity({ page: 0, size: 5 })
      .then((res) => {
        const items = (res?.content || []).map((log) => ({
          id: log.id,
          type: mapActivityType(log.eventType),
          company: log.companyName || '—',
          user: log.actorName || log.actorEmail || '—',
          time: timeAgo(log.createdAt),
          status: log.status === 'SUCCESS' ? 'success' : log.status === 'FAILED' ? 'warning' : 'pending',
        }));
        setRecentActivity(items);
      })
      .catch((err) => console.error('Failed to load recent activity:', err));
  }, []);

  const fetchHealth = async () => {
    try {
      const data = await adminApiClient.get('/api/admin/system-health');
      setHealthData(data);
      setHealthCheckedAt(new Date());
    } catch (err) {
      console.error('Failed to load system health:', err);
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    healthIntervalRef.current = setInterval(fetchHealth, 30000);
    return () => clearInterval(healthIntervalRef.current);
  }, []);

  // Format stat value (use real data if available, otherwise show N/A)
  const formatValue = (value) => {
    return value !== null && value !== undefined ? value.toString() : 'N/A';
  };

  // Platform-wide statistics
  const stats = [
    {
      name: 'Total Revenue',
      value: billingStats
        ? `$${Number(billingStats.totalRevenue || 0).toLocaleString()}`
        : statsData ? 'N/A' : '—',
      change: '—',
      trend: 'none',
      icon: DollarSign,
      color: 'bg-green-500',
      description: 'All time paid invoices'
    },
    {
      name: 'Active Users',
      value: statsData ? formatValue(statsData.totalPlatformUsers) : '—',
      change: '—',
      trend: 'none',
      icon: Users,
      color: 'bg-blue-500',
      description: 'Total platform users'
    },
    {
      name: 'Total Companies',
      value: statsData ? formatValue(statsData.totalCompanies) : '—',
      change: '—',
      trend: 'none',
      icon: Building2,
      color: 'bg-purple-500',
      description: 'Registered organizations'
    },
    {
      name: 'Support Tickets',
      value: statsData ? formatValue(statsData.openSupportTickets) : '—',
      change: '—',
      trend: 'none',
      icon: AlertTriangle,
      color: 'bg-orange-500',
      description: 'Open tickets awaiting response'
    },
  ];

  // ── Revenue & User Growth chart data ──────────────────────────────────────
  // Build last 6 months series from real growth + billing data.
  // "users" = cumulative company count (estimated going backwards from today's total).
  const last6 = getLast6Months();
  let cumCompanies = statsData?.totalCompanies || 0;
  const chartData = [...last6].reverse().map((m) => {
    const g = growthData.find((gp) => gp.year === m.year && gp.month === m.month);
    const r = billingStats?.monthlyRevenue?.find(
      (rp) => rp.year === m.year && rp.month === m.month
    );
    const point = {
      month: m.label,
      revenue: Math.round(r?.revenue || 0),
      users: Math.max(0, cumCompanies),
    };
    cumCompanies -= g?.newCompanies || 0;
    return point;
  }).reverse();

  // ── Subscription Plans pie chart data ─────────────────────────────────────
  // Merge CMMS + Chatbot plan distributions, add trial count.
  const trialCount = (subStats?.cmmsTrial || 0) + (subStats?.chatbotTrial || 0);
  const planMap = new Map();
  [...(subStats?.cmmsPlans || []), ...(subStats?.chatbotPlans || [])].forEach((p) => {
    planMap.set(p.planName, (planMap.get(p.planName) || 0) + Number(p.count));
  });
  const subscriptionData = [
    trialCount > 0 ? { name: 'Free Trial', value: trialCount, color: '#94a3b8' } : null,
    ...[...planMap.entries()].map(([name, value], i) => ({
      name,
      value,
      color: PLAN_COLORS[i % PLAN_COLORS.length],
    })),
  ].filter(Boolean);


  const getActivityIcon = (type) => {
    switch (type) {
      case 'signup': return <Users className="w-4 h-4" />;
      case 'upgrade': return <TrendingUp className="w-4 h-4" />;
      case 'support': return <MessageSquare className="w-4 h-4" />;
      case 'cancellation': return <AlertTriangle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-700';
      case 'warning': return 'bg-orange-100 text-orange-700';
      case 'pending': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getServiceStatusColor = (status) => {
    switch (status) {
      case 'UP':      return 'bg-green-500';
      case 'DOWN':    return 'bg-red-500';
      case 'DEGRADED':return 'bg-yellow-500';
      default:        return 'bg-gray-400';
    }
  };

  const getServiceStatusBadge = (status) => {
    switch (status) {
      case 'UP':      return 'text-green-700 bg-green-100';
      case 'DOWN':    return 'text-red-700 bg-red-100';
      case 'DEGRADED':return 'text-yellow-700 bg-yellow-100';
      default:        return 'text-gray-600 bg-gray-100';
    }
  };

  const overallBanner = () => {
    const s = healthData?.overallStatus;
    if (s === 'UP')       return { text: 'All Systems Operational', cls: 'text-green-700 bg-green-50 border-green-200', Icon: CheckCircle };
    if (s === 'DEGRADED') return { text: 'Partial Outage',          cls: 'text-yellow-700 bg-yellow-50 border-yellow-200', Icon: AlertTriangle };
    if (s === 'DOWN')     return { text: 'Major Outage',            cls: 'text-red-700 bg-red-50 border-red-200',    Icon: WifiOff };
    return { text: 'Checking…', cls: 'text-gray-600 bg-gray-50 border-gray-200', Icon: Loader };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Platform Overview</h1>
          <p className="text-gray-600 mt-1">Monitor and manage your entire SaaS platform</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            Export Report
          </button>
          <Link to="/admin/analytics" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
            View Analytics
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Loader className="w-8 h-8 text-primary-600 animate-spin" />
            <p className="text-gray-600">Loading dashboard statistics...</p>
          </div>
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <span>Failed to load dashboard statistics. Please refresh the page.</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.name} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    {stat.trend !== 'none' && (
                      <div className={`flex items-center gap-1 text-sm font-medium ${
                        stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stat.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {stat.change}
                      </div>
                    )}
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600 mb-2">{stat.name}</div>
                  <div className="text-xs text-gray-500">{stat.description}</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue & User Growth Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Revenue & User Growth</h2>
              <p className="text-sm text-gray-600">Last 6 months</p>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Revenue ($)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Companies</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} strokeWidth={2} name="Revenue ($)" />
              <Area type="monotone" dataKey="users" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.1} strokeWidth={2} name="Companies" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Subscription Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Subscription Plans</h2>
          {subscriptionData.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              No subscriptions yet
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <RePieChart>
                  <Pie
                    data={subscriptionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {subscriptionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RePieChart>
              </ResponsiveContainer>
              <div className="space-y-3 mt-4">
                {subscriptionData.map((plan) => (
                  <div key={plan.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: plan.color }}></div>
                      <span className="text-sm text-gray-600">{plan.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{plan.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
            <Link to="/admin/activity" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-4">
            {recentActivity.length === 0 && (
              <p className="text-center text-gray-400 py-6 text-sm">No recent activity</p>
            )}
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className={`w-10 h-10 ${getActivityColor(activity.status)} rounded-lg flex items-center justify-center`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{activity.company}</span>
                    <span className="text-sm text-gray-500">•</span>
                    <span className="text-sm text-gray-600 capitalize">{activity.type}</span>
                  </div>
                  <div className="text-sm text-gray-500">{activity.user}</div>
                </div>
                <div className="text-sm text-gray-500">{activity.time}</div>
              </div>
            ))}
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-gray-500" />
              <h2 className="text-xl font-bold text-gray-900">System Health</h2>
            </div>
            <button
              onClick={() => { setHealthLoading(true); fetchHealth(); }}
              disabled={healthLoading}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${healthLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Overall status banner */}
          {(() => {
            const { text, cls, Icon } = overallBanner();
            return (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium mb-4 ${cls}`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                {text}
              </div>
            );
          })()}

          {/* Service rows */}
          <div className="flex-1 space-y-3">
            {healthLoading && !healthData ? (
              <div className="flex justify-center py-8">
                <Loader className="w-6 h-6 text-gray-400 animate-spin" />
              </div>
            ) : (healthData?.services || []).map((svc) => (
              <div key={svc.key} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                {/* Status dot */}
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getServiceStatusColor(svc.status)}`} />

                {/* Name + uptime */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{svc.name}</div>
                  {svc.uptimeSeconds != null && (
                    <div className="text-xs text-gray-400">Up {formatUptime(svc.uptimeSeconds)}</div>
                  )}
                </div>

                {/* Right side: status badge + response time */}
                <div className="flex flex-col items-end gap-0.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getServiceStatusBadge(svc.status)}`}>
                    {svc.status}
                  </span>
                  {svc.responseTimeMs != null && svc.responseTimeMs > 0 && (
                    <span className="text-xs text-gray-400">{svc.responseTimeMs}ms</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Last checked */}
          {healthCheckedAt && (
            <p className="text-xs text-gray-400 mt-4 text-center">
              Last checked {timeAgo(healthCheckedAt)} · auto-refreshes every 30s
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
